import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Form, FormControl, InputGroup, Modal } from 'react-bootstrap'
import { FaCopy, FaDownload } from 'react-icons/fa'

interface DownloadModalProps {
    show: boolean
    onHide: () => void
    downloadInfo: { title: string, link: string }
}

const DownloadModal: React.FC<DownloadModalProps> = ({ show, onHide, downloadInfo }) => {
    const [copied, setCopied] = useState(false)
    const [copyError, setCopyError] = useState('')
    const copiedTimeout = useRef<number>()

    useEffect(() => {
        setCopied(false)
        setCopyError('')
        return () => {
            if (copiedTimeout.current) window.clearTimeout(copiedTimeout.current)
        }
    }, [show, downloadInfo.link])

    const handleCopyLink = async () => {
        setCopyError('')
        try {
            await navigator.clipboard.writeText(downloadInfo.link)
            setCopied(true)
            if (copiedTimeout.current) window.clearTimeout(copiedTimeout.current)
            copiedTimeout.current = window.setTimeout(() => setCopied(false), 2000)
        } catch {
            setCopyError('The link could not be copied automatically. Select it and copy it manually.')
        }
    }

    return (
        <Modal show={show} onHide={onHide} centered contentClassName="feed-download-modal">
            <Modal.Header closeButton>
                <div>
                    <Modal.Title>Download feed</Modal.Title>
                    <div className="text-muted small mt-1 text-break">{downloadInfo.title}</div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small">
                    Download the latest generated file or copy its stable URL for another system.
                </p>

                <Form.Label htmlFor="feed-download-link">Feed URL</Form.Label>
                <InputGroup className="feed-download-input-group">
                    <FormControl
                        id="feed-download-link"
                        value={downloadInfo.link}
                        readOnly
                        onFocus={(event) => event.currentTarget.select()}
                    />
                    <Button variant="outline-secondary" onClick={handleCopyLink} aria-label="Copy feed URL">
                        <FaCopy aria-hidden="true" className="me-2" />
                        {copied ? 'Copied' : 'Copy'}
                    </Button>
                </InputGroup>

                <div className="small mt-2 feed-copy-status" aria-live="polite">
                    {copied && <span className="text-success">Link copied to clipboard.</span>}
                </div>
                {copyError && <Alert variant="warning" className="mt-3 mb-0">{copyError}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>Close</Button>
                <Button href={downloadInfo.link} variant="primary">
                    <FaDownload aria-hidden="true" className="me-2" />
                    Download file
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default DownloadModal
